import { createEntityAdapter, createSelector } from "@reduxjs/toolkit";
import { apiSlice } from "./apiSlice";
import { buildQueryString } from "../utils/buildQueryString";

// --- Adapter ---
const productListAdapter = createEntityAdapter({
  selectId: (product) => product.id,
});
const initialState = productListAdapter.getInitialState();

// --- Slice ---
export const productListSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getProductList: builder.query({
      query: ({
        category,
        inventory_id,
        cursor = 0,
        pagination = 1,
        filters = {},
      }) => {
        const endpointPath = `${category}/inventory`;
        return buildQueryString(endpointPath, {
          inventory_id,
          pagination,
          cursor,
          ...filters,
        });
      },

      transformResponse: (responseData) => responseData.data,

      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}-${queryArgs.inventory_id}`,

      merge: (currentCache, newItems) => {
        productListAdapter.upsertMany(currentCache, newItems);
      },

      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.inventory_id !== previousArg?.inventory_id;
      },

      providesTags: (result, error, arg) => {
        if (!result) {
          return [{ type: "ProductList", id: arg.inventory_id }];
        }
        return [
          { type: "ProductList", id: arg.inventory_id },
          ...result.map((item) => ({ type: "Product", id: item.id })),
        ];
      },
    }),

    getProductById: builder.query({
      query: ({ category, request_id }) =>
        `${category}/inventory/details?request_id=${request_id}`,

      transformResponse: (responseData) => {
        // ✅ Only return the product details, pure JSON
        return responseData?.data ?? {};
      },

      async onCacheEntryAdded(
        { category, request_id },
        { cacheDataLoaded, updateCachedData, dispatch }
      ) {
        try {
          const productData = await cacheDataLoaded;

          // Ensure it's JSON-serializable
          const safeProductData = JSON.parse(JSON.stringify(productData));

          // Update this query's cached data
          updateCachedData((draft) => {
            Object.assign(draft, safeProductData);
          });

          // Push into product list cache
          if (safeProductData?.inventory_id) {
            dispatch(
              productListSlice.util.updateQueryData(
                "getProductList",
                { category, inventory_id: safeProductData.inventory_id },
                (draft) => {
                  productListAdapter.upsertOne(draft, safeProductData);
                }
              )
            );
          }
        } catch (error) {
          console.error("Cache update failed:", error);
        }
      },

      providesTags: (result, error, { request_id }) => [
        { type: "Product", id: request_id },
        { type: "ProductList" },
      ],
    }),
  }),
});

// --- Hooks ---
export const { useGetProductListQuery, useGetProductByIdQuery } =
  productListSlice;

// --- Selectors ---
const productListFilter = (state) => state.productListFilter;

const selectProductListResult = createSelector(
  [productListFilter, (state) => state],
  (filter, state) =>
    productListSlice.endpoints.getProductList.select({
      category: filter.category,
      inventory_id: filter.inventory_id,
      cursor: filter.cursor,
      pagination: filter.pagination,
      filters: {
        status: filter.status,
        search: filter.search,
      },
    })(state)
);

const selectProductListData = createSelector(
  [selectProductListResult],
  (productListResult) => productListResult?.data ?? initialState
);

export const {
  selectAll: selectProductList,
  selectById: selectProductById,
  selectIds: selectProductIds,
} = productListAdapter.getSelectors((state) => selectProductListData(state));
